'use client'

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useRouter } from 'next/navigation'
import { Modal } from '../ui/Modal'
import AuthForms, { type AuthedUser } from './AuthForms'

/* Action-gate: browsing stays free; CONTRIBUTING actions require an account.
   - useRequireAuth() wraps an action: if signed in it runs immediately; if not it
     opens a sign-up/sign-in modal and RESUMES the intent after auth.
   - A central capture-phase click interceptor gates the many "Register a masjid" /
     "Claim a masjid" links (navbar, footer, every page) without touching each one.
   Reuses the existing session (cookie via /api/auth/me) and the existing auth UI
   (AuthForms → /api/auth/login + /api/auth/register). No new backend. */

type Status = 'loading' | 'authed' | 'guest'
type Intent = { redirectTo?: string; onAuthed?: () => void; label?: string }

type Ctx = {
  status: Status
  user: AuthedUser | null
  requireAuth: (intent?: Intent) => void
}

const AuthGateContext = createContext<Ctx | null>(null)

// Logged-out clicks to these internal routes are intercepted into the gate modal.
const GATED_ROUTES = ['/onboard', '/masjid/claim']
const ROUTE_LABEL: Record<string, string> = {
  '/onboard': 'Create a free account to register your masjid.',
  '/masjid/claim': 'Create a free account to claim a masjid.',
}

export function AuthGateProvider({ children }: { children: ReactNode }) {
  const router = useRouter()
  const [status, setStatus] = useState<Status>('loading')
  const [user, setUser] = useState<AuthedUser | null>(null)
  const [open, setOpen] = useState(false)
  const [intentLabel, setIntentLabel] = useState<string | undefined>(undefined)
  const intentRef = useRef<Intent | null>(null)

  // One session check on mount (cookie-backed). Cheap; userId comes from the JWT.
  useEffect(() => {
    let alive = true
    fetch('/api/auth/me')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive) return
        if (d?.user?.id) { setUser(d.user); setStatus('authed') } else setStatus('guest')
      })
      .catch(() => { if (alive) setStatus('guest') })
    return () => { alive = false }
  }, [])

  const resume = useCallback((intent: Intent | null) => {
    if (!intent) return
    if (intent.onAuthed) { intent.onAuthed(); return }
    // redirectTo: only navigate if it's a different page (else they're already here).
    if (intent.redirectTo && intent.redirectTo.split('?')[0] !== window.location.pathname) {
      router.push(intent.redirectTo)
    }
  }, [router])

  const openGate = useCallback((intent?: Intent) => {
    intentRef.current = intent || null
    setIntentLabel(intent?.label)
    setOpen(true)
  }, [])

  const requireAuth = useCallback(async (intent?: Intent) => {
    let s = status
    // If the initial check hasn't resolved yet, resolve it on demand so we never
    // wrongly gate a signed-in user (or wrongly pass through a guest).
    if (s === 'loading') {
      try {
        const d = await fetch('/api/auth/me').then((r) => (r.ok ? r.json() : null))
        if (d?.user?.id) { setUser(d.user); setStatus('authed'); s = 'authed' } else { setStatus('guest'); s = 'guest' }
      } catch { setStatus('guest'); s = 'guest' }
    }
    if (s === 'authed') { resume(intent || null); return }
    openGate(intent)
  }, [status, resume, openGate])

  const handleAuthed = useCallback((u: AuthedUser) => {
    if (u?.id) { setUser(u); setStatus('authed') }
    setOpen(false)
    const intent = intentRef.current
    intentRef.current = null
    // Resume on the next tick so the modal-close paints before any navigation.
    setTimeout(() => resume(intent), 0)
  }, [resume])

  // Central interceptor: gate the (many) Register/Claim links centrally.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (status !== 'guest') return // only when confirmed logged-out
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const anchor = (e.target as HTMLElement | null)?.closest?.('a')
      if (!anchor) return
      const href = anchor.getAttribute('href') || ''
      if (!href.startsWith('/')) return // internal links only
      const path = href.split('?')[0].split('#')[0]
      if (!GATED_ROUTES.includes(path)) return
      e.preventDefault()
      e.stopPropagation()
      openGate({ redirectTo: href, label: ROUTE_LABEL[path] })
    }
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [status, openGate])

  return (
    <AuthGateContext.Provider value={{ status, user, requireAuth }}>
      {children}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Sign in to continue"
        description="Browsing is free. Contributing needs a free account."
        className="authgate-modal"
      >
        {intentLabel && <p className="entry-muted" style={{ margin: '0 0 12px', fontWeight: 600 }}>{intentLabel}</p>}
        <AuthForms initialMode="login" onAuthed={handleAuthed} />
      </Modal>
      <style jsx global>{`
        .authgate-modal { width: min(460px, 100%); }
        .authgate-modal .authforms .entry-form { gap: 12px; margin-top: 12px; }
      `}</style>
    </AuthGateContext.Provider>
  )
}

export function useAuthGate() {
  const ctx = useContext(AuthGateContext)
  if (!ctx) throw new Error('useAuthGate must be used within AuthGateProvider')
  return ctx
}

export function useRequireAuth() {
  return useAuthGate().requireAuth
}
